import type { Parent_Type_Enum } from '@risksmart-app/web-graphql-client/generated/graphql';

import Link from '@/components/link';
import { getFriendlyId } from '@/utils/friendlyId';

import type { FieldConfig, TableRecord } from '../types';

type FriendlyIdRow = { SequentialId?: null | number };

type ParentTypeArg<T> = ((item: T) => Parent_Type_Enum) | Parent_Type_Enum;

type FriendlyIdColumnArgs<T extends FriendlyIdRow & TableRecord> = {
  /**
   * Header label. Pass the translated string (e.g. `t('columns.id', { ns: 'common' })`)
   * — the factory does not call i18n hooks itself.
   */
  header: string;
  /**
   * The entity's `Parent_Type_Enum` used to look up the Friendly ID prefix.
   *
   * Static for most entities. Issues pass a getter `(item) => item.Type as Parent_Type_Enum`
   * because the 8 issue sub-types each have their own prefix (I, BL, CD, CT, GDPR, PCI,
   * RE, SAR).
   */
  parentType: ParentTypeArg<T>;
} & (
  | { hrefFor: (item: T) => string; onFollow?: never }
  | { hrefFor?: never; onFollow: (item: T) => void }
);

/**
 * Returns a default-visible, hyperlinked Friendly ID column for a register or
 * inline table.
 *
 * Hyperlink mode (most entities): pass `hrefFor` — the cell renders a Cloudscape
 * `Link` whose `href` matches the title cell's destination. Visual styling, focus
 * ring, visited state, and Cmd/Ctrl-click "open in new tab" are inherited from the
 * shared `Link` component.
 *
 * Modal mode (Causes only at time of writing): pass `onFollow` instead. Causes have
 * no detail page (`causeDetailsUrl` returns `/issues/{issueId}/causes` and ignores
 * the cause ID); the existing Title cell opens a modal via `onFollow`. The Friendly
 * ID column must match that behaviour for navigation parity.
 *
 * Empty cells: when `SequentialId` is `null`/`undefined` the cell renders a blank
 * string — no broken link. This is defensive; Node-registered entities always
 * carry a `SequentialId`, but inline tables aggregating cross-entity data may not.
 *
 * Sorting + export: `sortingField` is the raw numeric `SequentialId` (matches
 * pre-existing behaviour). `exportVal` returns the human-readable Friendly ID.
 *
 * @see RSP-5415 (foundation) — consumed by RSP-5416 / RSP-5417 / RSP-5418 / RSP-5419
 *      to apply the column to every register and inline table.
 */
export const friendlyIdColumn = <T extends FriendlyIdRow & TableRecord>(
  args: FriendlyIdColumnArgs<T>
): FieldConfig<T> => {
  const resolveParentType = (item: T): Parent_Type_Enum =>
    typeof args.parentType === 'function'
      ? args.parentType(item)
      : args.parentType;

  const labelFor = (item: T): string =>
    item.SequentialId == null
      ? ''
      : getFriendlyId(resolveParentType(item), item.SequentialId);

  return {
    header: args.header,
    sortingField: 'SequentialId',
    cell: (item) => {
      const label = labelFor(item);
      if (!label) {
        return '';
      }

      if (args.onFollow) {
        const onFollow = args.onFollow;

        return <Link onFollow={() => onFollow(item)}>{label}</Link>;
      }

      const hrefFor = args.hrefFor;

      return (
        <Link href={hrefFor(item)} variant={'secondary'}>
          {label}
        </Link>
      );
    },
    exportVal: (item) => labelFor(item),
  };
};
